import React, { useState, useEffect } from "react";
import { http } from "@/lib/api";
import { PageHeader, Loading, SectionTitle } from "@/components/common";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Settings2, Save, Plug } from "lucide-react";

const PROVIDERS = [
  ["dexscreener", "DexScreener", "Token / pair discovery & market data"],
  ["birdeye", "Birdeye", "Wallet intelligence, holders, PnL, first buyers"],
  ["helius", "Helius", "Wallet, transaction & transfer infrastructure"],
  ["goplus", "GoPlus", "Token security & malicious address intel"],
  ["rugcheck", "RugCheck", "Token / security analysis"],
  ["solana_rpc", "Solana RPC", "Direct on-chain data"],
];

export default function Settings() {
  const [s, setS] = useState(null);

  useEffect(() => { http.get("/settings").then((r) => setS(r.data.data)); }, []);

  const save = async () => {
    await http.put("/settings", s);
    toast.success("Settings saved");
  };

  if (!s) return <Loading />;

  const totalWeight = Object.values(s.momentum_weights).reduce((a, b) => a + b, 0);

  return (
    <div className="fade-in">
      <PageHeader
        icon={Settings2}
        title="Settings"
        subtitle="Configure providers, notifications, filters and score weighting."
        actions={<button data-testid="settings-save" onClick={save} className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold text-amber-50 hover:bg-amber-500"><Save className="h-4 w-4" /> Save</button>}
      />

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="pnut-card p-5">
          <SectionTitle right={<Plug className="h-4 w-4 text-slate-500" />}>Data Providers</SectionTitle>
          <p className="mb-3 text-[11px] text-slate-500">Toggle providers on when API keys are configured. While off, that provider's data is DEMO.</p>
          <div className="space-y-2">
            {PROVIDERS.map(([k, name, desc]) => (
              <label key={k} className="flex items-center justify-between rounded-md border border-slate-800 bg-void/50 px-3 py-2.5">
                <div>
                  <div className="text-sm font-medium text-slate-200">{name}</div>
                  <div className="text-[10px] text-slate-500">{desc}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`font-mono text-[9px] ${s.providers[k] ? "text-emerald-400" : "text-slate-600"}`}>{s.providers[k] ? "LIVE" : "DEMO"}</span>
                  <Switch data-testid={`provider-${k}`} checked={s.providers[k]} onCheckedChange={(v) => setS({ ...s, providers: { ...s.providers, [k]: v } })} />
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="pnut-card p-5">
          <SectionTitle>Momentum Score Weights <span className={`ml-2 font-mono text-xs ${totalWeight === 100 ? "text-emerald-400" : "text-amber-400"}`}>Σ {totalWeight}%</span></SectionTitle>
          <div className="space-y-2.5">
            {Object.entries(s.momentum_weights).map(([k, v]) => (
              <div key={k}>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">{k.replace(/_/g, " ")}</span>
                  <span className="font-mono text-slate-200">{v}%</span>
                </div>
                <input type="range" min={0} max={40} value={v} onChange={(e) => setS({ ...s, momentum_weights: { ...s.momentum_weights, [k]: Number(e.target.value) } })} className="w-full accent-amber-500" />
              </div>
            ))}
          </div>
        </div>

        <div className="pnut-card p-5">
          <SectionTitle>Notifications</SectionTitle>
          <div className="space-y-2">
            {Object.keys(s.notifications).map((k) => (
              <label key={k} className="flex items-center justify-between rounded-md border border-slate-800 bg-void/50 px-3 py-2.5 text-sm text-slate-300">
                <span className="capitalize">{k.replace("_", " ")}{(k === "email" || k === "webhook") && <span className="ml-1 text-[9px] text-slate-600">(later)</span>}</span>
                <Switch checked={s.notifications[k]} onCheckedChange={(v) => setS({ ...s, notifications: { ...s.notifications, [k]: v } })} disabled={k === "email" || k === "webhook"} />
              </label>
            ))}
          </div>
        </div>

        <div className="pnut-card p-5">
          <SectionTitle>Default Filters</SectionTitle>
          <div className="space-y-3">
            {Object.entries(s.filters).map(([k, v]) => (
              <div key={k}>
                <label className="mb-1 block font-mono text-[10px] uppercase text-slate-500">{k.replace(/_/g, " ")}</label>
                <input type="number" value={v} onChange={(e) => setS({ ...s, filters: { ...s.filters, [k]: Number(e.target.value) } })} className="w-full rounded-lg border border-slate-800 bg-void px-3 py-2 text-sm text-slate-200 outline-none focus:border-amber-600/50" />
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-md border border-slate-800 bg-void/50 p-3 text-[11px] leading-relaxed text-slate-500">
            <span className="font-semibold text-slate-300">Safety:</span> PnutScan never requests seed phrases, private keys or trading passwords, and never executes trades. Any future wallet connection is read-only.
          </div>
        </div>
      </div>
    </div>
  );
}
